import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Badge, Button, Field, Input, Text } from "@/components/ui";
import { apiErrorText } from "@/lib/api-error";
import {
  fetchAccessReport,
  fetchStudentAccess,
  signOutEverywhere,
  suspendStudent,
  unsuspendStudent,
  type AccessReport,
  type StudentAccess,
  type StudentAccessDetail,
} from "@/services/api/access-report";

/**
 * H4 (decisions.md §34): per student, the devices and places seen in the last 7 days, the last
 * sign-in and the sign-in history; a Flagged list; Sign out everywhere / Suspend / Unsuspend.
 * The flags are a signal to look closer — never proof of sharing (mobile-data addresses and their
 * places change many times a day). The IP address is shown here, to the school admin only.
 */
export function AccessReportAdmin() {
  const t = useTranslations("accessReport");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [report, setReport] = useState<AccessReport | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<StudentAccessDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // W-24: the student whose history was asked for last; a late answer for anyone else is dropped.
  const requestedId = useRef<string | null>(null);
  const hasReport = useRef(false);

  /** `true` when the report loaded; a failed reload keeps the report already shown. */
  const load = useCallback(async (): Promise<boolean> => {
    try {
      setReport(await fetchAccessReport());
      hasReport.current = true;
      setLoadFailed(false);
      return true;
    } catch {
      if (!hasReport.current) setLoadFailed(true);
      return false;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const formatTime = (iso: string) =>
    new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));

  async function openHistory(student: StudentAccess) {
    if (openId === student.userId) {
      requestedId.current = null;
      setOpenId(null);
      setDetail(null);
      return;
    }
    requestedId.current = student.userId;
    setOpenId(student.userId);
    setDetail(null);
    setDetailFailed(false);
    try {
      const answer = await fetchStudentAccess(student.userId);
      if (requestedId.current === student.userId) setDetail(answer);
    } catch {
      if (requestedId.current === student.userId) setDetailFailed(true);
    }
  }

  async function act(
    student: StudentAccess,
    action: "signOut" | "suspend" | "unsuspend",
  ): Promise<void> {
    const name = student.name ?? student.email;
    const confirmKey =
      action === "signOut"
        ? "confirmSignOut"
        : action === "suspend"
          ? "confirmSuspend"
          : "confirmUnsuspend";
    if (!window.confirm(t(confirmKey, { name }))) return;
    setBusyId(student.userId);
    setError(null);
    setNotice(null);
    try {
      if (action === "signOut") {
        const result = await signOutEverywhere(student.userId);
        setNotice(t("signedOutNotice", { name, count: result.signedOut }));
      } else if (action === "suspend") {
        await suspendStudent(student.userId);
        setNotice(t("suspendedNotice", { name }));
      } else {
        await unsuspendStudent(student.userId);
        setNotice(t("unsuspendedNotice", { name }));
      }
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "accessReport.actionFailed", { namespace: "admin.errors" }),
      );
    } finally {
      setBusyId(null);
    }
    if (!(await load())) setError((prev) => prev ?? t("loadFailed"));
  }

  if (loadFailed) {
    return (
      <p role="alert" className="text-sm text-red-600" data-testid="access-report-error">
        {t("loadFailed")}
      </p>
    );
  }
  if (!report) {
    return <Text tone="muted">{t("loading")}</Text>;
  }

  const query = filter.trim().toLowerCase();
  const visible = report.students.filter(
    (s) =>
      !query ||
      s.email.toLowerCase().includes(query) ||
      (s.name ?? "").toLowerCase().includes(query),
  );
  const flagged = report.students.filter((s) => s.flagged);

  const reasonText = (s: StudentAccess) =>
    s.flagReasons
      .map((r) =>
        r === "devices"
          ? t("reasonDevices", { count: s.devices7d, days: report.windowDays })
          : t("reasonCities", { count: s.cities7d, days: report.windowDays }),
      )
      .join(" · ");

  return (
    <div className="space-y-8" data-testid="access-report-admin">
      <div
        role="note"
        className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-2)] p-4 text-sm"
        data-testid="access-report-signal-note"
      >
        <p className="font-medium">{t("signalTitle")}</p>
        <p className="mt-1">
          {t("signalBody", {
            devices: report.thresholds.devices,
            cities: report.thresholds.cities,
            days: report.windowDays,
          })}
        </p>
        <p className="mt-1 text-[var(--text-muted)]">
          {t("retention", { days: report.retentionDays })}
        </p>
      </div>

      {notice ? (
        <p role="status" className="text-sm" data-testid="access-report-notice">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-600" data-testid="access-report-action-error">
          {error}
        </p>
      ) : null}

      <section aria-labelledby="access-flagged-heading" data-testid="access-report-flagged">
        <h2 id="access-flagged-heading" className="text-lg font-semibold">
          {t("flaggedHeading", { count: flagged.length })}
        </h2>
        {flagged.length === 0 ? (
          <Text tone="muted" className="mt-2">
            {t("flaggedNone", { days: report.windowDays })}
          </Text>
        ) : (
          <ul className="mt-3 space-y-2">
            {flagged.map((s) => (
              <li
                key={s.userId}
                className="rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
                data-testid={`access-flagged-${s.userId}`}
              >
                <span className="font-medium">{s.name ?? s.email}</span>{" "}
                <span className="text-[var(--text-muted)]">{s.email}</span>
                <span className="block">{reasonText(s)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="access-all-heading">
        <h2 id="access-all-heading" className="text-lg font-semibold">
          {t("studentsHeading", { count: report.students.length })}
        </h2>
        <Field label={t("filterLabel")} className="mt-3 max-w-sm">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="access-report-filter"
          />
        </Field>
        {visible.length === 0 ? (
          <Text tone="muted" className="mt-3">
            {t("noStudents")}
          </Text>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[var(--text-muted)]">
                <tr>
                  <th className="py-2 pr-3 font-medium">{t("colStudent")}</th>
                  <th className="py-2 pr-3 font-medium">
                    {t("colDevices", { days: report.windowDays })}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t("colCities", { days: report.windowDays })}
                  </th>
                  <th className="py-2 pr-3 font-medium">{t("colLastSignIn")}</th>
                  <th className="py-2 pr-3 font-medium">{t("colStatus")}</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">{t("colActions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => {
                  const name = s.name ?? s.email;
                  const busy = busyId === s.userId;
                  return (
                    <StudentRows
                      key={s.userId}
                      student={s}
                      open={openId === s.userId}
                      detail={openId === s.userId ? detail : null}
                      detailFailed={openId === s.userId && detailFailed}
                      formatTime={formatTime}
                    >
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-expanded={openId === s.userId}
                          aria-label={t("historyLabel", { name })}
                          onClick={() => void openHistory(s)}
                          data-testid={`access-history-toggle-${s.userId}`}
                        >
                          {openId === s.userId ? t("hideHistory") : t("showHistory")}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          aria-label={t("signOutLabel", { name })}
                          onClick={() => void act(s, "signOut")}
                          data-testid={`access-sign-out-${s.userId}`}
                        >
                          {t("signOutEverywhere")}
                        </Button>
                        {s.suspended ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            aria-label={t("unsuspendLabel", { name })}
                            onClick={() => void act(s, "unsuspend")}
                            data-testid={`access-unsuspend-${s.userId}`}
                          >
                            {t("unsuspend")}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy}
                            aria-label={t("suspendLabel", { name })}
                            onClick={() => void act(s, "suspend")}
                            data-testid={`access-suspend-${s.userId}`}
                          >
                            {t("suspend")}
                          </Button>
                        )}
                      </div>
                    </StudentRows>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StudentRows({
  student: s,
  open,
  detail,
  detailFailed,
  formatTime,
  children,
}: {
  student: StudentAccess;
  open: boolean;
  detail: StudentAccessDetail | null;
  detailFailed: boolean;
  formatTime: (iso: string) => string;
  children: React.ReactNode;
}) {
  const t = useTranslations("accessReport");
  const tDevices = useTranslations("auth.devices");
  return (
    <>
      <tr className="border-t border-[var(--border-subtle)]" data-testid={`access-row-${s.userId}`}>
        <td className="py-2 pr-3">
          <span className="font-medium">{s.name ?? s.email}</span>
          <span className="block text-[var(--text-muted)]">{s.email}</span>
        </td>
        <td className="py-2 pr-3" data-testid={`access-devices-${s.userId}`}>
          {s.devices7d}
        </td>
        <td className="py-2 pr-3" data-testid={`access-cities-${s.userId}`}>
          {s.cities7d}
        </td>
        <td className="py-2 pr-3">{s.lastSignInAt ? formatTime(s.lastSignInAt) : t("never")}</td>
        <td className="py-2 pr-3">
          <div className="flex flex-wrap gap-1">
            {s.flagged ? <Badge tone="warning">{t("flaggedBadge")}</Badge> : null}
            {s.suspended ? (
              <Badge tone="danger" data-testid={`access-suspended-${s.userId}`}>
                {t("suspendedBadge")}
              </Badge>
            ) : null}
            {!s.flagged && !s.suspended ? <Badge>{t("okBadge")}</Badge> : null}
          </div>
        </td>
        <td className="py-2">{children}</td>
      </tr>
      {open ? (
        <tr data-testid={`access-history-${s.userId}`}>
          <td colSpan={6} className="pb-4">
            {detailFailed ? (
              <p role="alert" className="text-sm text-red-600">
                {t("historyFailed")}
              </p>
            ) : !detail ? (
              <Text tone="muted">{t("loading")}</Text>
            ) : detail.history.length === 0 ? (
              <Text tone="muted">{t("historyNone")}</Text>
            ) : (
              <table className="w-full text-left text-xs">
                <caption className="sr-only">
                  {t("historyCaption", { name: s.name ?? s.email })}
                </caption>
                <thead className="text-[var(--text-muted)]">
                  <tr>
                    <th className="py-1 pr-3 font-medium">{t("colTime")}</th>
                    <th className="py-1 pr-3 font-medium">{t("colEvent")}</th>
                    <th className="py-1 pr-3 font-medium">{t("colPlace")}</th>
                    <th className="py-1 pr-3 font-medium">{t("colDevice")}</th>
                    <th className="py-1 font-medium">{t("colIp")}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.history.map((h, i) => (
                    <tr key={`${h.at}-${i}`} className="border-t border-[var(--border-subtle)]">
                      <td className="py-1 pr-3">{formatTime(h.at)}</td>
                      <td className="py-1 pr-3">
                        {h.event === "device_limit_reached"
                          ? t("eventDeviceLimit")
                          : h.event === "device_signed_out"
                            ? t("eventDeviceSignedOut")
                            : t("eventSignIn")}
                      </td>
                      <td className="py-1 pr-3">{h.place ?? t("placeUnknown")}</td>
                      <td className="py-1 pr-3">
                        {tDevices("label", {
                          browser: h.browser ?? tDevices("unknownBrowser"),
                          os: h.os ?? tDevices("unknownSystem"),
                        })}
                      </td>
                      <td className="py-1 font-mono">{h.ipAddress ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
